/**
 * JS errors + unhandled rejections → error_captured. Dedupe: identical message
 * within 5s dropped, and at most 10 error events per page view.
 *
 * Failed fetch/XHR (api_request_failed) is raised by auto/net.ts instead: it
 * wraps both APIs anyway for the waterfall, and one wrapper per API beats two
 * (DECISIONS #91). This module stays in the core bundle so a page always
 * reports thrown errors, chunk or no chunk.
 */
import { sanitizeUrl, SENSITIVE_QUERY_PATTERN } from '@l3ns/shared/constants';
import { st } from '../core/state';
import { enqueue } from '../core/track';
import { navH, now, on } from './util';

let cnt = 0;
let seen: Record<string, number> = {};
navH.push(() => {
	cnt = 0;
	seen = {};
});

function rep(name: string, msg: string, p: Record<string, unknown>): void {
	const n = now();
	if (st.auto_.errors && cnt < 10 && n - (seen[msg] || 0) >= 5000) {
		seen[msg] = n;
		cnt++;
		enqueue(name, p);
	}
}

/** Shared with auto/net.ts (via XApi) so both error paths obey one budget. */
export function repErrEvent(name: string, msg: string, p: Record<string, unknown>): void {
	rep(name, msg, p);
}

/** Read error fields defensively: rejection reasons can even be revoked proxies. */
function field(e: unknown, key: string): unknown {
	try {
		return (e as Record<string, unknown> | null)?.[key];
	} catch {}
}

const sensitive = new RegExp(
	SENSITIVE_QUERY_PATTERN.replace('|code', '') + '|pass|card|cvv|cvc|cookie|body|payload',
	'i',
);
// Read these first, even when a vendor puts them after a large context object.
const identifiers = ['name', 'code', 'errorCode', 'type', 'status', 'statusCode', 'message'];

/** Bounded diagnostic snapshot; never invokes getters or toJSON on payloads. */
function snapshot(e: unknown, depth = 0, budget = { left_: 100 }): unknown {
	try {
		let truncated = --budget.left_ < 0;
		if (e && typeof e == 'object') {
			truncated ||= depth == 3;
			if (e instanceof Node) return '[DOM node]';
			const array = Array.isArray(e) && !truncated;
			const out: Record<string, unknown> = array ? [] : Object.create(null);
			const keys = truncated
				? identifiers
				: [
						...new Set([
							...(array ? [] : identifiers.filter((key) => key in e)),
							...(e instanceof Error ? ['stack'] : Object.keys(e)),
						]),
					].slice(0, 20);
			for (const key of keys) {
				if (sensitive.test(key)) out[key] = 'REDACTED';
				else {
					const d =
						Object.getOwnPropertyDescriptor(e, key) ||
						Object.getOwnPropertyDescriptor(Object.getPrototypeOf(e) || {}, key);
					if (!d) continue;
					const v = d.value;
					if (truncated) {
						// Fixed scalar-only fallback: no recursion, getters, or fresh traversal.
						if (v != null && typeof v != 'object' && typeof v != 'function')
							out[key] = snapshot(v, -1);
					} else out[key] = d.get ? '[Getter]' : snapshot(v, depth + 1, budget);
				}
			}
			if (truncated) {
				if (!Object.keys(out).length) return '[Truncated]';
				out.truncated = true;
			}
			return out;
		}
		if (truncated) return '[Truncated]';
		return typeof e == 'string'
			? (/^https?:/.test(e) ? sanitizeUrl(e) : e).slice(0, depth < 0 ? 120 : 500)
			: typeof e == 'bigint' || typeof e == 'symbol'
				? String(e)
				: typeof e == 'function'
					? '[Function]'
					: e;
	} catch {
		return '[Unserializable]';
	}
}

/** {type, message, stack, details} from anything throwable (shared with error.capture). */
export function errProps(e: unknown): {
	type: string;
	message: string;
	stack?: string;
	details?: string;
} {
	const name = field(e, 'name');
	const message = field(e, 'message');
	const stack = field(e, 'stack');
	const details =
		e && typeof e == 'object' && !(typeof message == 'string' && typeof stack == 'string')
			? JSON.stringify(snapshot(e)).slice(0, 8192)
			: undefined;
	return {
		type: typeof name == 'string' && name ? name : 'Error',
		message: (typeof message == 'string' ? message : (details ?? String(e ?? 'unknown'))).slice(
			0,
			500,
		),
		stack: typeof stack == 'string' ? stack.slice(0, 8192) : undefined,
		details,
	};
}

function repErr(e: unknown, source: string, x?: Record<string, unknown>): void {
	const p = errProps(e);
	rep('error_captured', p.message, { ...p, source, ...x });
}

export function initErrors(): void {
	on(window, 'error', (ev: ErrorEvent) => {
		// non-capture listener ⇒ resource errors never arrive; guard anyway
		// filename/lineno/colno ride along: for parse errors (which produce no
		// in-script frames) the event position is the only attribution there is
		if (ev.error || ev.message)
			repErr(
				ev.error || { message: ev.message },
				'onerror',
				ev.filename
					? {
							filename: String(ev.filename).slice(0, 300),
							lineno: ev.lineno | 0,
							colno: ev.colno | 0,
						}
					: undefined,
			);
	});
	on(window, 'unhandledrejection', (ev: PromiseRejectionEvent) => repErr(ev.reason, 'rejection'));
}
